import {
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  HStack,
  Link,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { NavLink, Outlet } from "react-router-dom";
import {
  FiClock,
  FiHelpCircle,
  FiHome,
  FiLogOut,
  FiCalendar,
  FiFileText,
  FiUser,
} from "react-icons/fi";
import { logOut } from "../../services/authService";
import { useEssDashboard } from "../../services/essService";

const SIDEBAR_BG = "#0f172a";
const LABEL_MUTED = "#64748b";

const navShellProps = {
  display: "block",
  pl: 3,
  pr: 3,
  py: 2.5,
  mx: -1,
  borderRadius: "md",
  borderLeft: "3px solid transparent",
  fontSize: "sm",
  color: "gray.400",
  _hover: { bg: "whiteAlpha.100", color: "white", textDecoration: "none" },
  _activeLink: {
    bg: "whiteAlpha.150",
    color: "white",
    borderLeftColor: "blue.400",
    fontWeight: "600",
  },
};

function NavRow({ to, end, icon: IconC, children }) {
  return (
    <Link as={NavLink} to={to} end={end} {...navShellProps}>
      <HStack spacing={3}>
        <Box as="span" w="18px" display="flex" justifyContent="center" flexShrink={0}>
          <IconC size={18} />
        </Box>
        <Text>{children}</Text>
      </HStack>
    </Link>
  );
}

const EssShell = () => {
  const toast = useToast();
  const { essDashboardIsLoading, portal } = useEssDashboard();
  const hero = portal?.hero ?? {};
  const org = portal?.organization ?? {};
  const personal = portal?.personal ?? {};
  const displayName =
    hero.display_name || personal.email || "Employee";
  const companyLine = org.company_name || "";

  return (
    <Flex minH="100vh">
      <Box
        w={{ base: "200px", md: "228px" }}
        flexShrink={0}
        bg={SIDEBAR_BG}
        py={5}
        px={3}
        display="flex"
        flexDirection="column"
      >
        <VStack align="stretch" spacing={1} flex={1}>
          <Box px={2} pb={4}>
            <Heading size="sm" color="white" fontWeight="700" letterSpacing="-0.02em">
              Employee
            </Heading>
            <Text fontSize="xs" color="gray.400" noOfLines={2} mt={1.5} lineHeight="1.35">
              {essDashboardIsLoading ? "Loading…" : companyLine || "Portal"}
            </Text>
          </Box>

          <Text
            px={2}
            pt={2}
            pb={1}
            fontSize="10px"
            fontWeight="700"
            letterSpacing="0.12em"
            color={LABEL_MUTED}
          >
            MAIN
          </Text>
          <VStack align="stretch" spacing={0.5} px={1}>
            <NavRow to="/ess" end icon={FiHome}>
              Dashboard
            </NavRow>
            <NavRow to="/ess/leave" icon={FiCalendar}>
              Leave
            </NavRow>
            <NavRow to="/ess/overtime" icon={FiClock}>
              Overtime
            </NavRow>
            <NavRow to="/ess/documents" icon={FiFileText}>
              Documents
            </NavRow>
          </VStack>

          <Divider borderColor="whiteAlpha.200" my={4} />

          <Text
            px={2}
            pb={1}
            fontSize="10px"
            fontWeight="700"
            letterSpacing="0.12em"
            color={LABEL_MUTED}
          >
            ACCOUNT
          </Text>
          <VStack align="stretch" spacing={0.5} px={1}>
            <NavRow to="/ess/profile" icon={FiUser}>
              Profile
            </NavRow>
            <Button
              variant="unstyled"
              {...navShellProps}
              h="auto"
              textAlign="left"
              fontWeight="normal"
              onClick={() =>
                toast({
                  title: "Help & settings",
                  description: "Account settings will be available in a future update.",
                  status: "info",
                  duration: 5000,
                  isClosable: true,
                })
              }
            >
              <HStack spacing={3}>
                <Box as="span" w="18px" display="flex" justifyContent="center" flexShrink={0}>
                  <FiHelpCircle size={18} />
                </Box>
                <Text>Help & settings</Text>
              </HStack>
            </Button>
          </VStack>
        </VStack>

        <Box pt={4} mt="auto" borderTopWidth="1px" borderColor="whiteAlpha.150" px={1}>
          <Text fontSize="10px" color={LABEL_MUTED} px={2} mb={2} fontWeight="600">
            Signed in
          </Text>
          <Text fontSize="sm" fontWeight="600" color="white" px={2} noOfLines={2}>
            {displayName}
          </Text>
          <Button
            mt={3}
            size="sm"
            variant="outline"
            borderColor="whiteAlpha.300"
            color="gray.200"
            leftIcon={<FiLogOut />}
            w="100%"
            _hover={{ bg: "whiteAlpha.100", color: "white" }}
            onClick={() => logOut()}
          >
            Sign out
          </Button>
        </Box>
      </Box>

      <Box flex="1" p={{ base: 4, md: 8 }} overflow="auto" bg="#F8FAFC" minW={0}>
        <Outlet />
      </Box>
    </Flex>
  );
};

export default EssShell;
